import { toCsv } from '@/lib/csv';
import { downloadTextFile } from '@/lib/downloadTextFile';

import type { CourseHomepage } from '../courses/useCourse';

import { formatGradeValue } from './gradebookTypes';
import type { GradebookAssignment, GradebookStudent } from './gradebookTypes';

const ROSTER_HEADERS = ['Last Name', 'First Name', 'Email', 'Student ID'] as const;

function formatEarnedScore(score: number | null | undefined): string {
  // An assignment a student has not answered exports as 0 (not blank).
  if (score === null || score === undefined) {
    return '0';
  }

  return formatGradeValue(score);
}

/**
 * Build the roster CSV as a 2D array of strings mirroring the gradebook grid:
 * a header row, a "Max Score" row, then one row of raw earned scores per student.
 */
export function buildRosterCsvRows(
  assignments: GradebookAssignment[],
  students: GradebookStudent[],
): string[][] {
  const header: string[] = [...ROSTER_HEADERS];
  const maxRow: string[] = ['Max Score', '', '', ''];

  let preLectureTotalMax = 0;
  let homeworkTotalMax = 0;
  let participationTotalMax = 0;
  // Only emit the participation total column when some assignment uses it.
  const hasAnyParticipation = assignments.some(
    (assignment) => assignment.hasParticipation,
  );

  for (const assignment of assignments) {
    if (assignment.hasPreLecture) {
      header.push(`${assignment.title} PL`);
      maxRow.push(formatGradeValue(assignment.preLectureMaxScore));
      preLectureTotalMax += assignment.preLectureMaxScore;
    }

    if (assignment.hasHomework) {
      header.push(`${assignment.title} HW`);
      maxRow.push(formatGradeValue(assignment.homeworkMaxScore));
      homeworkTotalMax += assignment.homeworkMaxScore;
    }

    if (assignment.hasParticipation) {
      header.push(`${assignment.title} Coach`);
      maxRow.push(formatGradeValue(assignment.participationMaxScore));
      participationTotalMax += assignment.participationMaxScore;
    }
  }

  header.push('PL Total', 'HW Total');
  maxRow.push(
    formatGradeValue(preLectureTotalMax),
    formatGradeValue(homeworkTotalMax),
  );
  if (hasAnyParticipation) {
    header.push('Coach Total');
    maxRow.push(formatGradeValue(participationTotalMax));
  }

  const studentRows = students.map((student) => {
    const row: string[] = [
      student.lastName,
      student.firstName,
      student.email,
      student.instituteId,
    ];

    for (const assignment of assignments) {
      const score = student.assignmentScores[assignment.componentUid];

      if (assignment.hasPreLecture) {
        row.push(formatEarnedScore(score?.preLectureScore));
      }

      if (assignment.hasHomework) {
        row.push(formatEarnedScore(score?.homeworkScore));
      }

      if (assignment.hasParticipation) {
        row.push(formatEarnedScore(score?.participationScore));
      }
    }

    row.push(
      formatGradeValue(student.totalPreLectureScore),
      formatGradeValue(student.totalHomeworkScore),
    );
    if (hasAnyParticipation) {
      row.push(formatGradeValue(student.totalParticipationScore));
    }

    return row;
  });

  return [header, maxRow, ...studentRows];
}

/** Turn a course title into a filesystem-safe `.csv` filename. */
export function buildRosterCsvFilename(courseTitle: string): string {
  const safe = courseTitle.replace(/[/\\:*?"<>|]/g, ' ').replace(/\s+/g, ' ').trim();

  return `${safe || 'gradebook'}.csv`;
}

export function exportRosterCsv(
  course: Pick<CourseHomepage, 'title'>,
  assignments: GradebookAssignment[],
  students: GradebookStudent[],
): void {
  const csv = toCsv(buildRosterCsvRows(assignments, students));

  downloadTextFile(buildRosterCsvFilename(course.title), csv);
}
